import {type ComponentProps, createRef, type RefObject} from 'react';

import {action, makeObservable, observable} from 'mobx';

import type {TextInput} from '../../uikit/TextInput';
import type {Ref} from '../../uikit/TextInput/types';
import type {Nullable} from '../utility-types';
import type {Field, ValidationRule} from './types';

import {createField} from './createField';

export interface TextInputField extends Field<string> {
  focused: boolean;
  onBlur: ComponentProps<typeof TextInput>['onBlur'];
  onChange: ComponentProps<typeof TextInput>['onChange'];
  onFocus: ComponentProps<typeof TextInput>['onFocus'];
  ref: RefObject<Nullable<Ref>>;
}

export const createTextInputField = (
  name: string,
  {
    customValidate,
    defaultValue = '',
    extraFields = {},
    shouldTrimValue = false,
    validationRule,
  }: {
    customValidate?: (value: string) => boolean;
    defaultValue?: string;
    extraFields?: {[key: string]: any};
    shouldTrimValue?: boolean;
    validationRule?: ValidationRule;
  } = {},
) => {
  const formField = createField<string>(name, {
    customValidate,
    defaultValue,
    extraFields,
    validationRule,
  }) as TextInputField;

  formField.ref = createRef();

  formField.value = defaultValue;

  formField.focused = false;

  formField.onChange = e => {
    let newValue = e.payload?.text || '';

    if (shouldTrimValue) {
      newValue = newValue.trim();
    }

    formField.value = newValue;

    formField.validate();
  };

  formField.onFocus = () => {
    formField.focused = true;
  };

  formField.onBlur = () => {
    formField.focused = false;
  };

  return makeObservable(formField, {
    focused: observable,
    onBlur: action.bound,
    onChange: action.bound,
    onFocus: action.bound,
  });
};
